import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popover from 'material-ui/Popover';

const popoverStyles = {
    container: {
        padding: '16px 20px',
        maxWidth: '340px',
        fontSize: '12px',
        lineHeight: '1.5',
        fontFamily: 'Roboto, sans-serif',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        borderRadius: '8px',
        position: 'relative',
    },
    title: {
        fontWeight: '700',
        marginBottom: '12px',
        fontSize: '14px',
        color: '#333',
    },
    row: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '8px',
    },
    icon: {
        width: '16px',
        marginRight: '8px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '13px',
        marginTop: '1px',
    },
    label: {
        flex: 1,
    },
    divider: {
        borderTop: '1px solid #eee',
        margin: '12px 0',
    },
    detailText: {
        marginTop: '2px',
        fontSize: '11px',
        color: '#666',
    },
    buttonContainer: {
        marginTop: '8px',
    },
    button: {
        padding: '5px 12px',
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px',
        border: 'none',
        backgroundColor: '#0070f3',
        color: '#ffffff',
        cursor: 'pointer',
        transition: 'background-color 0.2s, transform 0.1s',
        outline: 'none',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
};

const StageButton = ({ label, onClick, primaryColor, hoverColor }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isActive, setIsActive] = useState(false);

    return (
        <button
            type="button"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setIsActive(false);
            }}
            onMouseDown={() => setIsActive(true)}
            onMouseUp={() => setIsActive(false)}
            onClick={onClick}
            style={{
                ...popoverStyles.button,
                backgroundColor: isHovered ? hoverColor : primaryColor,
                transform: isActive ? 'scale(0.97)' : 'scale(1)',
            }}
        >
            {label}
        </button>
    );
};

const DeleteButton = ({ onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <button
            type="button"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
            style={{
                padding: '4px 8px',
                fontSize: '10px',
                fontWeight: '500',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: isHovered ? 'rgba(244, 67, 54, 0.08)' : 'transparent',
                color: isHovered ? '#d32f2f' : '#888888',
                cursor: 'pointer',
                transition: 'all 0.2s',
                outline: 'none',
                boxShadow: 'none',
                textDecoration: 'underline',
            }}
        >
            Delete Geo Match
        </button>
    );
};

const CloseButton = ({ onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <button
            type="button"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
            style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                width: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
                color: isHovered ? '#000000' : '#888888',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '11px',
                lineHeight: '1',
                padding: '0',
                transition: 'all 0.15s ease-in-out',
                outline: 'none',
            }}
        >
            ✕
        </button>
    );
};

const pastelColors = {
    green: { border: 'rgba(76, 175, 80, 0.24)', color: '#2e7d32', bg: 'rgba(76, 175, 80, 0.04)' },
    yellow: { border: 'rgba(245, 127, 23, 0.24)', color: '#e65100', bg: 'rgba(245, 127, 23, 0.04)' },
    red: { border: 'rgba(244, 67, 54, 0.24)', color: '#c62828', bg: 'rgba(244, 67, 54, 0.04)' },
};

function getScoreColors(score) {
    if (score == null) return null;
    if (score >= 80) return pastelColors.green;
    if (score >= 50) return pastelColors.yellow;
    return pastelColors.red;
}

const GeoMatchPopover = ({
    open,
    anchorEl,
    onClose,
    area,
    router,
    onStage,
    onClear,
}, context) => {
    const { muiTheme } = context;
    const primaryColor = muiTheme ? muiTheme.palette.primary1Color : '#0070f3';
    const hoverColor = muiTheme ? muiTheme.palette.primary2Color : '#0051a8';

    const recognized = area && area.namesMatchGeo && area.namesMatchGeo.recognized;
    const portInfo = recognized && recognized.port;
    const regionInfo = recognized && recognized.region;

    if (!area || !recognized) {
        return null;
    }

    const handleStage = (isPort, target) => {
        onClose();
        if (onStage) {
            onStage(isPort, target);
        }
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            targetOrigin={{ horizontal: 'left', vertical: 'top' }}
            onRequestClose={onClose}
            useLayerForClickAway
        >
            <div style={popoverStyles.container}>
                <CloseButton onClick={onClose} />
                <div style={popoverStyles.title}>Geo Match Recognized Info</div>

                {portInfo && (
                    <div style={popoverStyles.row}>
                        <span
                            style={{
                                ...popoverStyles.icon,
                                color: portInfo.matched ? '#4CAF50' : '#F44336',
                            }}
                        >
                            {portInfo.matched ? '✓' : '✗'}
                        </span>
                        <div style={popoverStyles.label}>
                            <div>Port: <strong>{portInfo.realName || 'N/A'}</strong></div>
                            <div style={popoverStyles.detailText}>
                                Score:
                                {portInfo.score != null ? (() => {
                                    const scoreVal = Math.round(portInfo.score * 100);
                                    const styleInfo = getScoreColors(scoreVal);
                                    return (
                                        <strong style={{
                                            color: styleInfo.color,
                                            background: styleInfo.bg,
                                            border: `1px solid ${styleInfo.border}`,
                                            padding: '1px 5px',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            marginLeft: '4px',
                                            display: 'inline-block',
                                            fontWeight: '600'
                                        }}>
                                            {scoreVal}%
                                        </strong>
                                    );
                                })() : <strong> N/A</strong>}
                                {portInfo.matchedName && ` (Matched: "${portInfo.matchedName}")`}
                            </div>
                            {portInfo.matched && portInfo.refReal && portInfo.refReal !== area._id && (
                                <div style={popoverStyles.buttonContainer}>
                                    <StageButton
                                        label="Stage Port"
                                        onClick={() => handleStage(true, portInfo)}
                                        primaryColor={primaryColor}
                                        hoverColor={hoverColor}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {portInfo && regionInfo && <div style={popoverStyles.divider} />}

                {regionInfo && (
                    <div style={popoverStyles.row}>
                        <span
                            style={{
                                ...popoverStyles.icon,
                                color: regionInfo.matched ? '#4CAF50' : '#F44336',
                            }}
                        >
                            {regionInfo.matched ? '✓' : '✗'}
                        </span>
                        <div style={popoverStyles.label}>
                            <div>Region: <strong>{regionInfo.realName || 'N/A'}</strong></div>
                            <div style={popoverStyles.detailText}>
                                Score:
                                {regionInfo.score != null ? (() => {
                                    const scoreVal = Math.round(regionInfo.score * 100);
                                    const styleInfo = getScoreColors(scoreVal);
                                    return (
                                        <strong style={{
                                            color: styleInfo.color,
                                            background: styleInfo.bg,
                                            border: `1px solid ${styleInfo.border}`,
                                            padding: '1px 5px',
                                            borderRadius: '3px',
                                            fontSize: '10px',
                                            marginLeft: '4px',
                                            display: 'inline-block',
                                            fontWeight: '600'
                                        }}>
                                            {scoreVal}%
                                        </strong>
                                    );
                                })() : <strong> N/A</strong>}
                                {regionInfo.matchedName && ` (Matched: "${regionInfo.matchedName}")`}
                            </div>
                            {regionInfo.matched && regionInfo.refReal && regionInfo.refReal !== area._id && (
                                <div style={popoverStyles.buttonContainer}>
                                    <StageButton
                                        label="Stage Region"
                                        onClick={() => handleStage(false, regionInfo)}
                                        primaryColor={primaryColor}
                                        hoverColor={hoverColor}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {(portInfo || regionInfo) && (
                    <React.Fragment>
                        <div style={popoverStyles.divider} />
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
                            <DeleteButton
                                onClick={() => {
                                    onClose();
                                    if (onClear) {
                                        onClear(area);
                                    }
                                }}
                            />
                        </div>
                    </React.Fragment>
                )}
            </div>
        </Popover>
    );
};

GeoMatchPopover.contextTypes = {
    muiTheme: PropTypes.object,
};

export default GeoMatchPopover;



// WEBPACK FOOTER //
// ./app/components/Regions/Dashboard/FilterAreas/GeoMatchPopover.jsx